import { useEffect, useMemo, useState } from "react";
import { buscarClienteConsumidorFinal, listarClientes } from "../../servicios/clientes";
import { obtenerComprobantesCuentaCorriente } from "../../servicios/cuentaCorrienteApi";
import { useStoreHome } from "../Home/useStoreHome";
import { esVentaCuentaCorriente } from "./cuentaCorrienteUtils";
import "./cuentaCorriente.css";

const dinero = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" });
const fecha = (valor) => valor ? new Intl.DateTimeFormat("es-AR").format(new Date(valor)) : "-";
const comprobante = (item) => `${item.CodigoComprobante || ""} ${String(item.PuntoVenta || 0).padStart(5, "0")}-${String(item.Numero || 0).padStart(8, "0")}`;

export default function CuentaCorriente() {
  const empresaId = useStoreHome((state) => state.EmpresaId) || Number(localStorage.getItem("EmpresaId"));
  const [clientes, setClientes] = useState([]);
  const [personaId, setPersonaId] = useState(0);
  const [documentos, setDocumentos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  const ventas = useMemo(() => documentos.filter(esVentaCuentaCorriente), [documentos]);
  const totalVentas = useMemo(() => ventas.reduce((total, venta) => total + Number(venta.Total || 0), 0), [ventas]);
  const clienteSeleccionado = useMemo(
    () => clientes.find((cliente) => Number(cliente.Id) === Number(personaId)),
    [clientes, personaId],
  );

  useEffect(() => {
    let vigente = true;
    listarClientes({ EmpresaId: empresaId }).then((respuesta) => {
      if (!vigente) return;
      if (respuesta.error) setError(respuesta.error);
      else {
        const lista = respuesta.data?.Lista || [];
        const consumidorFinal = buscarClienteConsumidorFinal(lista);
        setClientes(consumidorFinal
          ? lista.filter((cliente) => Number(cliente.Id) !== Number(consumidorFinal.Id))
          : lista);
      }
    });
    return () => { vigente = false; };
  }, [empresaId]);

  const cargar = async (id = personaId) => {
    if (!Number(id)) {
      setDocumentos([]);
      return;
    }
    setCargando(true);
    setError("");
    const respuesta = await obtenerComprobantesCuentaCorriente({ empresaId, personaId: Number(id) });
    if (respuesta.error) setError(respuesta.error);
    else setDocumentos(respuesta.data?.Lista || []);
    setCargando(false);
  };

  useEffect(() => {
    if (personaId) cargar(personaId);
  }, [personaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const seleccionarCliente = (id) => {
    setPersonaId(Number(id));
    setDocumentos([]);
    setError("");
  };

  return <main className="cc-pagina cc-reporte-ventas">
    <header className="cc-cabecera">
      <div className="cc-cabecera-principal">
        <span className="cc-icono" aria-hidden="true"><i className="pi pi-shopping-cart" /></span>
        <div><h1>Ventas por cliente</h1><p>Consultá las ventas realizadas a un cliente.</p></div>
      </div>
      {clienteSeleccionado && <div className="cc-cliente-chip"><i className="pi pi-user" /><span><small>Cliente seleccionado</small><strong>{clienteSeleccionado.sDescripcion || clienteSeleccionado.sCodigo}</strong></span></div>}
    </header>

    <div className="cc-contenido">
      <section className="cc-panel cc-consulta">
        <div className="cc-panel-cabecera">
          <span className="cc-seccion-icono"><i className="pi pi-search" /></span>
          <div><small>Consulta</small><h2>Seleccioná un cliente</h2><p>El reporte se actualiza al cambiar la selección.</p></div>
        </div>
        <div className="cc-selector">
          <label><span>Cliente</span><select value={personaId || ""} disabled={cargando} onChange={(evento) => seleccionarCliente(evento.target.value)}><option value="">Seleccione un cliente</option>{clientes.map((cliente) => <option key={cliente.Id} value={cliente.Id}>{[cliente.sCodigo, cliente.sDescripcion].filter(Boolean).join(" - ")}</option>)}</select></label>
          <button type="button" onClick={() => cargar()} disabled={!personaId || cargando}><i className={cargando ? "pi pi-spin pi-spinner" : "pi pi-refresh"} /> {cargando ? "Actualizando..." : "Actualizar"}</button>
        </div>
      </section>

      {error && <div className="cc-error" role="alert"><i className="pi pi-exclamation-circle" /><span>{error}</span><button type="button" onClick={() => setError("")} aria-label="Cerrar error">×</button></div>}

      {!personaId && !error && <section className="cc-panel cc-inicial"><span><i className="pi pi-users" /></span><h2>Elegí un cliente para comenzar</h2><p>Vas a ver exclusivamente las ventas registradas a su nombre.</p></section>}
      {personaId > 0 && cargando && <section className="cc-panel cc-cargando"><i className="pi pi-spin pi-spinner" /><span>Cargando ventas...</span></section>}

      {personaId > 0 && !cargando && !error && <section className="cc-panel cc-seccion">
        <div className="cc-panel-cabecera cc-reporte-cabecera">
          <span className="cc-seccion-icono"><i className="pi pi-shopping-cart" /></span>
          <div><small>Reporte</small><h2>Ventas realizadas</h2><p>{ventas.length} venta(s) registrada(s).</p></div>
          <div className="cc-reporte-total"><span>Total vendido</span><strong>{dinero.format(totalVentas)}</strong></div>
        </div>
        <div className="cc-tabla-wrap"><table><thead><tr><th>Fecha</th><th>Comprobante</th><th className="numero">Total</th></tr></thead><tbody>{ventas.map((venta) => <tr key={venta.DocumentoId}><td data-label="Fecha">{fecha(venta.Fecha)}</td><td data-label="Comprobante"><strong>{comprobante(venta)}</strong></td><td data-label="Total" className="numero"><strong>{dinero.format(venta.Total || 0)}</strong></td></tr>)}</tbody></table>{!ventas.length && <div className="cc-vacio"><i className="pi pi-inbox" /><strong>No hay ventas registradas</strong><span>Este cliente no tiene ventas en cuenta corriente.</span></div>}</div>
      </section>}
    </div>
  </main>;
}
